/**
 * Reads the entitlement status out of a Supabase access token's `bh_claims`.
 *
 * Shared by the Sleepio and Spark email callbacks, which both decide "is this a
 * returning user?" from it. They each carried their own copy and drifted apart once
 * already (SD-2335 / PAT-1281), so there is one implementation now.
 *
 * Returns undefined when the token can't be read, which every caller treats as
 * "not a returning user".
 */
// eslint-disable-next-line import/prefer-default-export
export const getEntitlementStatus = (accessToken: string): string | undefined => {
  try {
    // JWT payloads are base64url, which uses `-` and `_` where standard base64 uses `+`
    // and `/`. atob() rejects those, so a token containing either would decode to
    // undefined here — reading as "not a returning user" and sending a login down the
    // sign-up branch.
    //
    // Padding does not need restoring: an unpadded base64 segment's length mod 4 is
    // always 0, 2 or 3, never 1, and WHATWG forgiving-base64 (browsers, Node, jsdom)
    // accepts all three. Only length mod 4 === 1 throws, which a well-formed JWT
    // segment cannot produce.
    const base64 = accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return payload.bh_claims?.products?.[0]?.status;
  } catch {
    return undefined;
  }
};
